'use client';

import {
	SignUpPage_GetVerificationTokenQuery,
	SignUpPage_GetInvitationWhereQuery,
	InviteEnum,
	SignUpPage_GetOrganizationFromInvitationQuery,
	useSignUpPage_LogoutMutation,
} from '#types';
import { gql } from '@apollo/client';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ChooseUserType } from './ChooseUserType';
import { CreateCoreUser } from './CreateCoreUser';
import { RequestVerificationEmail } from './RequestVerificationEmail';
import styles from './SignUpPage.module.scss';
import MuiModal from '../../mui/MuiModal/MuiModal';
import { Button } from '../../blocks/Button';
import { BrandWordmark } from '../BrandWordmark';
import { RecaptchaInterstitial } from '../RecaptchaInterstitial';
import {
	WmbaCampaignBrand,
	WmbaCampaignIntro,
	WmbaOffer,
	WmbaSupport,
} from './WmbaCampaign';
import { FlattenedUser } from '../../types/user';
import { logout as reduxLogout } from '../../redux/actions';
import { Spinner } from '../Spinner';

const isPlaywrightTest = process.env.NEXT_PUBLIC_PLAYWRIGHT_TEST === 'true';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const signUpPage_logout = gql`
	mutation SignUpPage_logout {
		logout
	}
`;

export type SignUpProps = {
	invitation: SignUpPage_GetInvitationWhereQuery['getInvitationWhere'];
	verificationToken?: SignUpPage_GetVerificationTokenQuery['getVerificationToken'];
	isRegistered: boolean;
	email?: string;
	promoCode?: string;
	organization: SignUpPage_GetOrganizationFromInvitationQuery['getOrganizationFromInvitation'];
};

export const SignUpPage: React.FC<SignUpProps> = ( {
	invitation,
	verificationToken,
	isRegistered,
	email,
	promoCode,
	organization,
} ) => {
	const dispatch = useDispatch();
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const logoutStarted = useRef( false );
	const [ logout ] = useSignUpPage_LogoutMutation();
	const [ showEmailVerificationModal, setShowEmailVerificationModal ] =
		useState( false );
	const [ showAlreadyRegisteredModal, setShowAlreadyRegisteredModal ] =
		useState( false );
	const [ step, setStep ] = useState( 0 );
	const [ password, setPassword ] = useState( '' );
	const [ confirmPassword, setConfirmPassword ] = useState( '' );
	const [ recaptchaShown, setRecaptchaShown ] = useState( isPlaywrightTest );

	const isWmbaCampaign =
		promoCode === 'WMBA26' && !verificationToken && !invitation;
	const headerCopy = useMemo( () => {
		if (
			invitation?.invitationType === InviteEnum.NewOrgMember &&
			organization?.name
		) {
			return `You have been invited to join ${ organization.name }`;
		}
		return 'Welcome to RPC!';
	}, [ invitation, organization ] );

	useEffect( () => {
		if ( isRegistered ) {
			setShowAlreadyRegisteredModal( true );
		}
	}, [ isRegistered ] );

	useEffect( () => {
		const logoutAuthenticatedUser = async () => {
			if ( !user?.isLoggedIn || logoutStarted.current ) {
				return;
			}

			logoutStarted.current = true;
			await logout();
			dispatch( reduxLogout( false ) );
		};

		void logoutAuthenticatedUser();
	}, [
		dispatch,
		logout,
		user?.isLoggedIn
	] );

	if ( user?.isLoggedIn ) {
		return <Spinner />;
	}

	return (
		<>
			{ !recaptchaShown ? (
				<RecaptchaInterstitial onClose={ () => setRecaptchaShown( true ) } />
			) : step !== 1 ? (
				<Box
					className={ `${ styles.container } ${
						isWmbaCampaign ? styles.campaignContainer : ''
					}` }
					component='main'
				>
					<Box
						className={ `${ styles.content } ${
							isWmbaCampaign ? styles.campaignContent : ''
						}` }
					>
						<Box
							className={ `${ styles.intro } ${
								isWmbaCampaign ? styles.campaignIntro : ''
							}` }
						>
							{ isWmbaCampaign ? (
								<>
									<WmbaCampaignBrand />
									<WmbaCampaignIntro />
								</>
							) : (
								<>
									<BrandWordmark className={ styles.brand } />
									<Box className={ styles.copy }>
										<Typography className={ styles.heading }>
											Run your event business
											<br />
											with clarity and control.
										</Typography>
										<Typography className={ styles.subtext }>
											From signed contracts to final payments, keep every
											booking on track in one place.
										</Typography>
									</Box>
								</>
							) }
						</Box>
						<Box
							className={ `${ styles.dialogue } ${
								isWmbaCampaign ? styles.campaignDialogue : ''
							}` }
						>
							<Typography variant='h1' data-cy='welcome-to-rpc'>
								{ isWmbaCampaign ? 'Welcome to Rock Paper Coin!' : headerCopy }
							</Typography>
							{ invitation?.invitationType !== InviteEnum.NewOrgMember ? (
								<Typography className={ styles.subText }>
									Create your account and start saving time today.
								</Typography>
							) : null }
							{ isWmbaCampaign ? <WmbaOffer /> : null }
							{ verificationToken || invitation ? (
								<CreateCoreUser
									email={ email }
									invitation={ invitation }
									onSuccess={ () => setStep( 1 ) }
									password={ password }
									setPassword={ setPassword }
									confirmPassword={ confirmPassword }
									setConfirmPassword={ setConfirmPassword }
								/>
							) : (
								<RequestVerificationEmail
									email={ email }
									promoCode={ promoCode }
									submitLabel={ isWmbaCampaign ? 'Start free trial' : undefined }
									onSuccess={ () => setShowEmailVerificationModal( true ) }
								/>
							) }
							<Typography className={ styles.signInLink }>
								Already have an account? <Link href='/Login'>Log in</Link>
							</Typography>
							{ isWmbaCampaign ? <WmbaSupport /> : null }
						</Box>
					</Box>
				</Box>
			) : verificationToken ? (
				<ChooseUserType
					verificationToken={ verificationToken }
					promoCode={ promoCode }
					coreUserFields={ {
						email,
						password,
						rePassword: confirmPassword,
					} }
				/>
			) : null }
			<MuiModal
				muiModalProps={ {
					open: showEmailVerificationModal,
					onClose: () => setShowEmailVerificationModal( false ),
					slotProps: { paper: { 'data-cy': 'email-verification-modal' } },
				} }
				title='Verification email sent'
			>
				<Typography>
					Please check your email for a link to verify your account before
					proceeding. Peek in your spam folder if you don't see it!
				</Typography>
			</MuiModal>
			<MuiModal
				muiModalProps={ {
					open: showAlreadyRegisteredModal,
					onClose: () => setShowAlreadyRegisteredModal( false ),
					slotProps: { paper: { 'data-cy': 'already-registered-modal' } },
				} }
				title='Already Registered'
				controls={
					<Link href='/'>
						<Button className={ styles.signIn }>Sign In</Button>
					</Link>
				}
			>
				<Typography>
					You have already set your password. You can use that password to log
					in by clicking the button below.
				</Typography>
				<br />
				<Typography>
					(If you don't remember your password, click on the "Forgot your
					password?" text below the password field on the Sign In page.)
				</Typography>
			</MuiModal>
		</>
	);
};
